import { memo, useState, useEffect, useMemo } from "react";
import { InitializeVisitDialog } from "./InitializeVisitDialog";
import { useCachedQuery } from "@/hooks/useCachedQuery";
import { ReadContractorVisit } from "@/types/contractorVisit";
import { useParams, useNavigate } from "react-router-dom";
import { ContractorPanelVisitService } from "../services/contractorPanelVisitService";
import { MobileListLoading } from "@/components/mobile-list/components/MobileListLoading";
import { ContractorPanelVisitsHeader } from "./ContractorPanelVisitsHeader";
import { useTranslation } from "react-i18next";
import { RoundedIconButton } from "@/components/ui/rounded-icon-button";
import { ArrowLeft } from "lucide-react";
import { PATH_CONTRACTOR_VISITS, Module } from "@/constants";
import { VisitDetails } from "./VisitDetails";
import { useGeolocationPermissions } from "@/hooks/useGeolocationPermissions";
import { useInitializeVisit } from "../hooks/useInitializeVisit";
import { useCompleteVisit } from "../hooks/useCompleteVisit";

const ContractorPanelVisitsEditorComp = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [dialogOpen, setDialogOpen] = useState(false);
  const { contractorVisitId } = useParams();
  const visitId = contractorVisitId as string;
  const {
    isEnabled: isGeolocationEnabled,
    isSupported: isGeolocationSupported,
  } = useGeolocationPermissions();

  const query = useCachedQuery<ReadContractorVisit>({
    queryKey: [Module.ContractorPanelVisits, visitId],
    queryFn: async () => ContractorPanelVisitService.getVisit(visitId),
  });

  const initializeVisitMutation = useInitializeVisit(visitId);
  const completeVisitMutation = useCompleteVisit(visitId);

  const shouldShowDialog = useMemo(() => {
    return (
      query.isSuccess &&
      query.data &&
      !query.data.start_datetime &&
      query.data.confirmable_by_contractor &&
      query.data.edit_allowed &&
      isGeolocationSupported &&
      isGeolocationEnabled
    );
  }, [
    query.isSuccess,
    query.data,
    isGeolocationSupported,
    isGeolocationEnabled,
  ]);

  useEffect(() => {
    if (shouldShowDialog) {
      setDialogOpen(true);
    }
  }, [query.isSuccess, query.data, shouldShowDialog]);

  const handleConfirm = () => {
    initializeVisitMutation.mutate(undefined, {
      onSuccess: () => {
        setDialogOpen(false);
      },
    });
  };

  const handleCancel = () => {
    setDialogOpen(false);
    navigate(PATH_CONTRACTOR_VISITS);
  };

  const handleCompleteVisit = (
    startDatetime?: string,
    endDatetime?: string,
  ) => {
    completeVisitMutation.mutate({ startDatetime, endDatetime });
  };

  if (query.isLoading) {
    return (
      <div className="p-4 h-screen flex flex-col">
        <ContractorPanelVisitsHeader title={t("contractorVisits.title.edit")} />
        <div className="flex-1 flex items-end pb-20">
          <MobileListLoading
            label={t("contractorVisits.loading")}
            className="w-full"
          />
        </div>
      </div>
    );
  }

  if (query.isError) {
    return (
      <div className="p-4">
        <ContractorPanelVisitsHeader title={t("contractorVisits.title.edit")} />
        <div className="flex justify-center py-8">
          <p className="text-destructive">
            {t("contractorVisits.error.loadFailed")}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 pb-20">
      <ContractorPanelVisitsHeader
        title={t("contractorVisits.title.edit")}
        rightElement={
          <RoundedIconButton
            variant="outline"
            size="sm"
            onClick={() => navigate(PATH_CONTRACTOR_VISITS)}
          >
            <ArrowLeft />
            <span className="sr-only">{t("contractorVisits.backToList")}</span>
          </RoundedIconButton>
        }
      />
      {query.data?.confirmable_by_contractor && query.data.edit_allowed && (
        <InitializeVisitDialog
          open={dialogOpen}
          onOpenChange={setDialogOpen}
          onConfirm={handleConfirm}
          onCancel={handleCancel}
          isLoading={initializeVisitMutation.isPending}
        />
      )}

      {query.data && (
        <VisitDetails
          visit={query.data}
          onInitializeVisit={() => setDialogOpen(true)}
          onCompleteVisit={handleCompleteVisit}
          loading={
            initializeVisitMutation.isPending || completeVisitMutation.isPending
          }
        />
      )}
    </div>
  );
};

export const ContractorPanelVisitsEditor = memo(
  ContractorPanelVisitsEditorComp,
);
